import axios from "axios";
import api, { ApiResponse } from "../api";
import {
    EquipmentListModel,
    EquipmentDetailModel,
} from "./models";
import { PageResponseModel } from "../common/models";

export const REQUEST_ID: {
    [key: string]: string;
} = {
    GET_EQUIPMENT_LIST: "get_equipment_list",
    GET_EQUIPMENT_DETAIL: "get_equipment_detail",
    GET_DOWNLOAD_CARRIER: "get_download_equipment",
};

class EquipmentService {
    private source: any;
    private static instance: EquipmentService;
    private constructor() {
        this.source = {};
    }
    static getInstance(): EquipmentService {
        if (!EquipmentService.instance) {
            EquipmentService.instance = new EquipmentService();
        }
        return EquipmentService.instance;
    };

    public cancelRequest = (requestId: string) => {
        this.source[requestId] && this.source[requestId].cancel();
    }

    public getEquipmentTypes = async () => {
        return await api.get("/equipments/equipment-types");
    };

    public getequipmentList = async (
        search?: string,
        page?: number,
        type?: string,
        status?: string
    ): Promise<ApiResponse<PageResponseModel<EquipmentListModel>>> => {

        const params = new URLSearchParams();

        if (search && search.trim() !== "") params.append("search", search);
        if (type && type.trim() !== "") params.append("type", type);
        if (status && status.trim() !== "") params.append("status", status);

        if (page !== undefined) params.append("page", page.toString());

        this.cancelRequest(REQUEST_ID.GET_EQUIPMENT_LIST);
        this.source[REQUEST_ID.GET_EQUIPMENT_LIST] = axios.CancelToken.source();

        return await api.get("/equipments", {
            params,
            cancelToken: this.source[REQUEST_ID.GET_EQUIPMENT_LIST].token
        });
    };


    public getEquipmentDetails = async (
        equipmentNumber: string,
        page?: number
    ): Promise<ApiResponse<PageResponseModel<EquipmentDetailModel>>> => {
        const params = new URLSearchParams();

        if (page !== undefined) {
            params.append("page", page.toString());
        }

        this.cancelRequest(REQUEST_ID.GET_EQUIPMENT_DETAIL);
        this.source[REQUEST_ID.GET_EQUIPMENT_DETAIL] = axios.CancelToken.source();

        return await api.get(`/equipments/equipment/${equipmentNumber}`, {
            params,
            cancelToken: this.source[REQUEST_ID.GET_EQUIPMENT_DETAIL].token
        });
    };

    public createEquipment = async (payload: any) => {
        try {
            const res = await api.post("/equipment", payload);
            if (res.status !== 200) {
                throw new Error(res.data?.message || "Something went wrong");
            }
            return res;
        } catch (err: any) {
            const msg =
                err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                "Failed to create equipment";
            throw new Error(msg);
        }
    };

    public updateEquipment = async (id: string, payload: any) => {
        try {
            const res = await api.put(`/equipments/${id}`, payload);
            if (res.status !== 200) {
                throw new Error(res.data?.message || "Something went wrong");
            }
            return res;
        } catch (err: any) {
            const msg =
                err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                "Failed to update equipment";
            throw new Error(msg);
        }
    };

    public deleteEquipment = async (id: string) => {
        try {
            const res = await api.delete(`/equipments/${id}`);
            if (res.status !== 200) {
                throw new Error(res.data?.message || "Something went wrong");
            }
            return res;
        } catch (err: any) {
            const msg =
                err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                "Failed to delete equipment";
            throw new Error(msg);
        }
    };


    public getDownloadCSVEquipment = async (
        search: string,
    ): Promise<Blob> => {
        const params = new URLSearchParams();

        if (search && search.trim() !== "") {
            params.append("search", search);
        }

        this.cancelRequest(REQUEST_ID.GET_DOWNLOAD_CARRIER);
        this.source[REQUEST_ID.GET_DOWNLOAD_CARRIER] = axios.CancelToken.source();

        const response = await api.get("/equipments/export", {
            params,
            cancelToken: this.source[REQUEST_ID.GET_DOWNLOAD_CARRIER].token,
            responseType: "blob",
        });

        return response.data;
    };

};

export default EquipmentService;